import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, Loader2, BarChart3, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import AnalisisService, { FiltrosDisponibles } from '../services/analisisService';
import InformeAvanceService, { InformeAvanceDatos } from '../services/informeAvanceService';

/**
 * Vista móvil de Informe de Avance
 * Adaptada para pantallas pequeñas con formulario wizard y tablas simplificadas
 */
export const InformeAvanceMovilView: React.FC = () => {
  const { t } = useTranslation();
  // Estados principales
  const [filtrosDisponibles, setFiltrosDisponibles] = useState<FiltrosDisponibles | null>(null);
  const [informeActual, setInformeActual] = useState<InformeAvanceDatos | null>(null);
  const [cargandoFiltros, setCargandoFiltros] = useState(true);
  const [cargandoInforme, setCargandoInforme] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);

  // Estados del formulario
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [predioId, setPredioId] = useState<number | null>(null);

  // Estados para configuración de PDF
  const [mostrarConfigPDF, setMostrarConfigPDF] = useState(false);
  const [configPDF, setConfigPDF] = useState({
    zoom: 13,
    titulo: ''
  });

  // Tab expandido
  const [tabExpandido, setTabExpandido] = useState<'reportes' | 'combustible' | null>(null);

  useEffect(() => {
    cargarFiltrosDisponibles();
  }, []);

  const cargarFiltrosDisponibles = async () => {
    try {
      setCargandoFiltros(true);
      const filtros = await AnalisisService.obtenerFiltrosDisponibles();
      setFiltrosDisponibles(filtros);

      if (filtros.rango_fechas.fecha_min && filtros.rango_fechas.fecha_max) {
        setFechaDesde(filtros.rango_fechas.fecha_min);
        setFechaHasta(filtros.rango_fechas.fecha_max);
      }
    } catch (error) {
      toast.error(t('errors.load_error'));
    } finally {
      setCargandoFiltros(false);
    }
  };

  const validarFormulario = (): boolean => {
    if (!fechaDesde || !fechaHasta) {
      toast.error(t('advance_report.date_from') + ' / ' + t('advance_report.date_to'));
      return false;
    }

    if (!predioId) {
      toast.error(t('advance_report.select_predio'));
      return false;
    }

    return true;
  };

  const ejecutarInforme = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      setCargandoInforme(true);

      const resultado = await InformeAvanceService.obtenerDatosInformeAvance({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        predios_ids: [predioId!],
      });

      setInformeActual(resultado);
      const predio0 = (resultado as any)?.reportes_por_predio?.[0];
      toast.success(`✅ ${predio0?.metricas?.resumen?.total_reportes ?? 0} reportes encontrados`);
    } catch (error: any) {
      toast.error(error.message || t('errors.unknown_error'));
    } finally {
      setCargandoInforme(false);
    }
  };

  const abrirConfiguracionPDF = () => {
    const predioSeleccionado = filtrosDisponibles?.predios.find(p => p.id === predioId);
    setConfigPDF({
      zoom: 13,
      titulo: `Informe - ${predioSeleccionado?.codigo || 'Predio'} - ${new Date().toLocaleDateString()}`
    });
    setMostrarConfigPDF(true);
  };

  const generarPDF = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      setGenerandoPDF(true);
      setMostrarConfigPDF(false);
      toast.loading(t('pdf.generating'), { id: 'informe-progress' });

      await InformeAvanceService.generarInformeAvancePDF({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        predios_ids: [predioId!],
        zoom: configPDF.zoom,
        titulo: configPDF.titulo || undefined,
      });

      toast.success(t('common.success'), { id: 'informe-progress' });
    } catch (error: any) {
      toast.error(error.message || t('errors.unknown_error'), { id: 'informe-progress' });
    } finally {
      setGenerandoPDF(false);
    }
  };

  const combustible0 = (informeActual as any)?.reportes_por_predio?.[0]?.combustible || [];

  if (cargandoFiltros) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-6 h-6 text-blue-600" />
            <h1 className="text-lg font-semibold text-gray-900">{t('advance_report.title')}</h1>
          </div>
        </div>
      </div>

      {/* Overlay de carga para PDF */}
      {generandoPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-2xl max-w-sm w-full mx-4">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pdf.generating')}</h3>
              <p className="text-sm text-gray-600">{t('common.processing')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Configuración PDF */}
      {mostrarConfigPDF && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
          <div className="bg-white rounded-t-lg w-full shadow-2xl">
            <div className="px-4 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{t('pdf.options_title')}</h3>
            </div>

            <div className="px-4 py-4 space-y-4 max-h-96 overflow-y-auto">
              {/* Título */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdf.custom_title')}:</label>
                <input
                  type="text"
                  value={configPDF.titulo}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, titulo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('pdf.custom_title') + '...'}
                />
              </div>

              {/* Zoom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pdf.map_zoom')}: {configPDF.zoom}</label>
                <input
                  type="range"
                  min="8"
                  max="18"
                  value={configPDF.zoom}
                  onChange={(e) => setConfigPDF(prev => ({ ...prev, zoom: parseInt(e.target.value) }))}
                  className="w-full"
                />
              </div>
            </div>

            {/* Botones */}
            <div className="px-4 py-4 border-t flex gap-2">
              <button
                onClick={() => setMostrarConfigPDF(false)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>

              <button
                onClick={generarPDF}
                className="flex-1 px-4 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
              >
                {t('pdf.generate')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenido Principal */}
      <div className="px-4 py-4 space-y-4">
        {/* Panel de Filtros */}
        <div className="bg-white rounded-lg shadow-sm border p-4 space-y-3">
          <h2 className="text-sm font-medium text-gray-900">{t('analysis.filters')}</h2>

          {/* Predio */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('advance_report.predio')} <span className="text-red-500">*</span>
            </label>
            <select
              value={predioId || ''}
              onChange={(e) => setPredioId(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.select')}</option>
              {filtrosDisponibles?.predios.map((predio) => (
                <option key={predio.id} value={predio.id}>
                  {predio.codigo} - {predio.descripcion}
                </option>
              ))}
            </select>
          </div>

          {/* Fecha Desde */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('advance_report.date_from')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Fecha Hasta */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              {t('advance_report.date_to')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Botón Ejecutar */}
          <button
            onClick={ejecutarInforme}
            disabled={cargandoInforme}
            className="w-full py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {cargandoInforme ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('advance_report.generating')}
              </>
            ) : (
              <>
                <BarChart3 className="w-4 h-4" />
                {t('advance_report.generate')}
              </>
            )}
          </button>
        </div>

        {/* Resultados */}
        {informeActual ? (
          <div className="space-y-4">
            {/* Resumen */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">{t('analysis.results')}</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">{t('advance_report.predio')}</p>
                  <p className="text-sm font-bold text-blue-900 truncate">{(informeActual as any)?.reportes_por_predio?.[0]?.predio?.codigo}</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-green-600 font-medium">{t('advance_report.reports_table')}</p>
                  <p className="text-lg font-bold text-green-900">{(informeActual as any)?.reportes_por_predio?.[0]?.metricas?.resumen?.total_reportes}</p>
                </div>
                <div className="bg-purple-50 p-3 rounded-lg">
                  <p className="text-xs text-purple-600 font-medium">{t('common.hours')}</p>
                  <p className="text-lg font-bold text-purple-900">{((informeActual as any)?.reportes_por_predio?.[0]?.metricas?.resumen?.total_horas || 0).toFixed(1)}</p>
                </div>
                <div className="bg-orange-50 p-3 rounded-lg">
                  <p className="text-xs text-orange-600 font-medium">{t('common.area')} (ha)</p>
                  <p className="text-lg font-bold text-orange-900">{((informeActual as any)?.reportes_por_predio?.[0]?.metricas?.resumen?.total_area_hectareas || 0).toFixed(1)}</p>
                </div>
              </div>
            </div>

            {/* Botón Generar PDF */}
            <button
              onClick={abrirConfiguracionPDF}
              className="w-full py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <FileText className="w-4 h-4" />
              {t('advance_report.download_pdf')}
            </button>

            {/* Tabla de Reportes (colapsible) */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <button
                onClick={() => setTabExpandido(tabExpandido === 'reportes' ? null : 'reportes')}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 border-b hover:bg-gray-100 transition-colors"
              >
                <h3 className="text-sm font-medium text-gray-900">Reportes ({((informeActual as any)?.reportes_por_predio?.[0]?.reportes || []).length})</h3>
                <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${tabExpandido === 'reportes' ? 'rotate-180' : ''}`} />
              </button>

              {tabExpandido === 'reportes' && (
                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                  {((informeActual as any)?.reportes_por_predio?.[0]?.reportes || []).map((reporte: any, idx: number) => (
                    <div key={idx} className="border rounded-lg p-3 space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-900">{t('reports.report_number')} {reporte.numero}</span>
                        <span className="text-gray-600">{reporte.fecha_str}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('reports.machine')}:</span>
                        <span className="font-medium text-gray-900">{reporte.maquina_descripcion}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('reports.operator')}:</span>
                        <span className="font-medium text-gray-900">{reporte.operador_descripcion}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('common.hours')}:</span>
                        <span className="font-medium text-gray-900">{reporte.horas.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('common.area')}:</span>
                        <span className="font-medium text-gray-900">{reporte.area.toFixed(2)} ha</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tabla de Combustible (colapsible) */}
            {combustible0.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <button
                  onClick={() => setTabExpandido(tabExpandido === 'combustible' ? null : 'combustible')}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 border-b hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-sm font-medium text-gray-900">Combustible ({combustible0.length})</h3>
                  <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${tabExpandido === 'combustible' ? 'rotate-180' : ''}`} />
                </button>

                {tabExpandido === 'combustible' && (
                  <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                    {combustible0.map((combustible: any, idx: number) => (
                      <div key={idx} className="border rounded-lg p-3 space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-900">{t('reports.report_number')} {combustible.numero}</span>
                          <span className="text-gray-600">{combustible.fecha_str}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('reports.machine')}:</span>
                          <span className="font-medium text-gray-900">{combustible.maquina_descripcion}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('supplies.quantity')}:</span>
                          <span className="font-medium text-gray-900">{combustible.cantidad.toFixed(2)} {combustible.unidad}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
            <FileText className="mx-auto h-10 w-10 text-gray-400 mb-3" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">{t('advance_report.no_reports')}</h3>
            <p className="text-xs text-gray-500">{t('advance_report.generate')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InformeAvanceMovilView;
